import { useState } from 'react'

interface Dimension {
  id: string
  name: string
  description: string
  weight: number
  criteria: string[]
}

interface Rubric {
  id: string
  name: string
  description: string
  appliesTo: {
    families: string[]
    levels: string[]
  }
  dimensions: Dimension[]
  isDefault: boolean
}

// Mock data - in production this comes from API
const MOCK_RUBRICS: Rubric[] = [
  {
    id: '1',
    name: 'Technical Interview',
    description: 'Standard evaluation for technical problem-solving and coding ability',
    appliesTo: {
      families: ['Engineering'],
      levels: ['All levels'],
    },
    dimensions: [
      {
        id: 'd1',
        name: 'Problem Solving',
        description: 'Ability to break down problems and develop solutions',
        weight: 30,
        criteria: [
          'Clarifies requirements before starting',
          'Identifies edge cases',
          'Considers tradeoffs between approaches',
          'Iterates on solution when stuck',
        ],
      },
      {
        id: 'd2',
        name: 'Code Quality',
        description: 'Cleanliness, readability, and maintainability of code',
        weight: 25,
        criteria: [
          'Uses meaningful variable names',
          'Writes modular, reusable code',
          'Handles errors appropriately',
          'Follows language conventions',
        ],
      },
      {
        id: 'd3',
        name: 'Communication',
        description: 'Clarity in explaining thought process and decisions',
        weight: 20,
        criteria: [
          'Thinks aloud while solving',
          'Asks clarifying questions',
          'Explains approach before coding',
          'Responds well to hints and feedback',
        ],
      },
      {
        id: 'd4',
        name: 'Technical Depth',
        description: 'Understanding of fundamentals and advanced concepts',
        weight: 25,
        criteria: [
          'Demonstrates knowledge of data structures',
          'Understands time/space complexity',
          'Applies appropriate algorithms',
          'Shows awareness of system constraints',
        ],
      },
    ],
    isDefault: true,
  },
  {
    id: '2',
    name: 'System Design',
    description: 'Evaluation for architectural thinking and system design ability',
    appliesTo: {
      families: ['Engineering'],
      levels: ['Senior', 'Staff', 'Principal'],
    },
    dimensions: [
      {
        id: 'd1',
        name: 'Requirements Gathering',
        description: 'Ability to clarify scope and constraints',
        weight: 20,
        criteria: [
          'Asks about scale and traffic',
          'Clarifies functional requirements',
          'Identifies non-functional requirements',
          'Makes reasonable assumptions',
        ],
      },
      {
        id: 'd2',
        name: 'High-Level Design',
        description: 'Architecture and component design',
        weight: 30,
        criteria: [
          'Proposes clear architecture',
          'Identifies major components',
          'Explains data flow',
          'Considers APIs and interfaces',
        ],
      },
      {
        id: 'd3',
        name: 'Deep Dives',
        description: 'Ability to go deep on specific components',
        weight: 30,
        criteria: [
          'Can detail database schema',
          'Explains caching strategy',
          'Addresses bottlenecks',
          'Considers failure scenarios',
        ],
      },
      {
        id: 'd4',
        name: 'Tradeoffs',
        description: 'Understanding of engineering tradeoffs',
        weight: 20,
        criteria: [
          'Discusses consistency vs availability',
          'Considers cost implications',
          'Weighs complexity vs functionality',
          'Addresses security concerns',
        ],
      },
    ],
    isDefault: false,
  },
  {
    id: '3',
    name: 'Behavioral Assessment',
    description: 'Evaluation of soft skills, culture fit, and past experiences',
    appliesTo: {
      families: ['All families'],
      levels: ['All levels'],
    },
    dimensions: [
      {
        id: 'd1',
        name: 'Leadership',
        description: 'Ability to lead and influence others',
        weight: 25,
        criteria: [
          'Provides concrete examples of leading',
          'Shows ownership of outcomes',
          'Demonstrates mentorship',
          'Handles disagreements constructively',
        ],
      },
      {
        id: 'd2',
        name: 'Collaboration',
        description: 'Working effectively with others',
        weight: 25,
        criteria: [
          'Examples of cross-team work',
          'Handles feedback gracefully',
          'Builds relationships',
          'Communicates proactively',
        ],
      },
      {
        id: 'd3',
        name: 'Growth Mindset',
        description: 'Attitude toward learning and improvement',
        weight: 25,
        criteria: [
          'Acknowledges mistakes and learnings',
          'Seeks feedback actively',
          'Shows continuous improvement',
          'Adapts to new situations',
        ],
      },
      {
        id: 'd4',
        name: 'Problem Navigation',
        description: 'How they approach challenges',
        weight: 25,
        criteria: [
          'Breaks down ambiguous problems',
          'Perseveres through difficulty',
          'Seeks appropriate help',
          'Balances pragmatism and idealism',
        ],
      },
    ],
    isDefault: true,
  },
]

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  )
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
  )
}

function DocumentDuplicateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
    </svg>
  )
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function RubricCard({ rubric, onEdit }: { rubric: Rubric; onEdit: () => void }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="border border-surface-200 rounded-xl overflow-hidden bg-white">
      {/* Header */}
      <div className="px-5 py-4 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-surface-900">{rubric.name}</h3>
            {rubric.isDefault && (
              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-brand-50 text-brand-700 border border-brand-200">
                Default
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-surface-500">{rubric.description}</p>
          
          {/* Tags */}
          <div className="mt-3 flex flex-wrap gap-2">
            {rubric.appliesTo.families.map((family) => (
              <span key={family} className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-surface-100 text-surface-600">
                {family}
              </span>
            ))}
            {rubric.appliesTo.levels.map((level) => (
              <span key={level} className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700">
                {level}
              </span>
            ))}
          </div>
        </div>
        
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onEdit}
            className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg transition-colors"
            title="Edit rubric"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg transition-colors"
            title="Duplicate rubric"
          >
            <DocumentDuplicateIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg transition-colors"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Dimensions */}
      {expanded && (
        <div className="border-t border-surface-200 bg-surface-50">
          <div className="p-5">
            <p className="text-sm font-medium text-surface-700 mb-4">
              Evaluation dimensions ({rubric.dimensions.length})
            </p>
            <div className="space-y-4">
              {rubric.dimensions.map((dimension) => (
                <div key={dimension.id} className="bg-white border border-surface-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <h4 className="font-medium text-surface-900">{dimension.name}</h4>
                      <p className="text-sm text-surface-500">{dimension.description}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-brand-50 text-brand-700">
                        <span className="text-sm font-semibold">{dimension.weight}%</span>
                      </div>
                      <p className="text-xs text-surface-400 mt-1">weight</p>
                    </div>
                  </div>
                  
                  <div className="pt-3 border-t border-surface-100">
                    <p className="text-xs font-medium text-surface-500 uppercase tracking-wide mb-2">Criteria</p>
                    <div className="grid sm:grid-cols-2 gap-1.5">
                      {dimension.criteria.map((criterion, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-surface-600">
                          <span className="mt-1.5 w-1 h-1 rounded-full bg-surface-400 shrink-0" />
                          {criterion}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function EvaluationRubricsSettings() {
  const [rubrics] = useState(MOCK_RUBRICS)

  return (
    <div className="divide-y divide-surface-100">
      {/* Header */}
      <div className="p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-surface-900">Evaluation Rubrics</h2>
            <p className="mt-1 text-surface-600">
              Define how candidates are scored across different interview types. Rubrics ensure consistent, structured feedback.
            </p>
          </div>
          <button
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 transition-colors shadow-sm"
          >
            <PlusIcon className="w-4 h-4" />
            Create rubric
          </button>
        </div>
      </div>

      {/* How it works */}
      <div className="p-6 sm:p-8 bg-surface-50">
        <div className="flex gap-4">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-brand-700" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-brand-900">How rubrics work</h3>
            <p className="mt-1 text-sm text-brand-700">
              Each rubric defines dimensions (what to evaluate) with weighted criteria. When an evaluator submits feedback, 
              they score against these dimensions. The system aggregates scores to surface signal conflicts and measure 
              evaluator alignment.
            </p>
          </div>
        </div>
      </div>

      {/* Rubrics list */}
      <div className="p-6 sm:p-8">
        <div className="space-y-4">
          {rubrics.map((rubric) => (
            <RubricCard key={rubric.id} rubric={rubric} onEdit={() => {}} />
          ))}
        </div>
      </div>

      {/* Empty state */}
      {rubrics.length === 0 && (
        <div className="p-12 text-center">
          <div className="w-12 h-12 rounded-full bg-surface-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-surface-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75" />
            </svg>
          </div>
          <h3 className="font-medium text-surface-900 mb-1">No rubrics defined</h3>
          <p className="text-surface-500 text-sm mb-6 max-w-sm mx-auto">
            Create evaluation rubrics to standardize how candidates are assessed across your organization.
          </p>
          <button
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Create first rubric
          </button>
        </div>
      )}
    </div>
  )
}

